import React, { forwardRef } from 'react';
import { Box, ResponsiveValue } from '@chakra-ui/react';

interface IControlButtonProps {
    onClick: () => void;
    ariaLabel: string;
    /**
     * Marks the button unavailable without taking it out of the tab order — `aria-disabled`
     * rather than `disabled`, so it stays focusable and can still be a focus target.
     *
     * Unlike the native attribute this doesn't stop activation on its own, so the click is
     * dropped here rather than left to each caller to guard: a button that announces itself
     * unavailable and then acts anyway is worse than either state alone.
     */
    ariaDisabled?: boolean;
    bg?: string;
    w?: ResponsiveValue<string>;
    h?: ResponsiveValue<string>;
    children: React.ReactNode;
}

/**
 * Forwards its ref so a caller can make the button a focus target — the slideshow editor points
 * its modal's `initialFocusRef` at the play/pause button rather than the close button.
 */
const ControlButton = forwardRef<HTMLButtonElement, IControlButtonProps>(
    (
        {
            onClick,
            ariaLabel,
            ariaDisabled = false,
            bg = 'transparent',
            w = 'auto',
            h = 'auto',
            children
        },
        ref
    ) => (
        <Box
            ref={ref}
            as="button"
            onClick={ariaDisabled ? undefined : onClick}
            aria-label={ariaLabel}
            aria-disabled={ariaDisabled || undefined}
            p="8px"
            rounded="4px"
            bg={bg}
            border="none"
            cursor={ariaDisabled ? 'not-allowed' : 'pointer'}
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            w={w}
            h={h}
            // `color` is repeated here on purpose. The client gallery's global stylesheet styles
            // bare `button` elements like anchors, and its `button:hover` rule (specificity 0-1-1)
            // outranks the generated class this component's `color` prop produces (0-1-0) — which
            // turned the icon brand-blue on hover in client galleries but not in studio. Restating
            // it under `_hover` matches that specificity, and the emotion styles land later.
            //
            // An unavailable button keeps its resting background, so it stops presenting itself
            // as something to press.
            _hover={
                ariaDisabled
                    ? { bg, color: 'white' }
                    : {
                          bg: bg === 'transparent' ? 'blackAlpha.600' : 'blackAlpha.800',
                          color: 'white'
                      }
            }
        >
            {children}
        </Box>
    )
);

ControlButton.displayName = 'ControlButton';

export default ControlButton;
